/**
 * "My Waitlist" on the account page.
 *
 * Renders whatever /apps/etg/waitlist/mine returns. That endpoint goes through
 * the Shopify App Proxy, which signs the request and supplies a verified
 * customer id, so this file never sends an identity of its own and cannot be
 * pointed at somebody else's queue by editing the DOM.
 *
 * Read-only by design. The buttons that change a queue (pass, reduce, leave)
 * are a later phase and are not wired here: they need the hourly reconcile job
 * to be paid-safe first, since a customer can pass an allocation and still pay
 * the invoice sitting in their inbox.
 *
 * Two rules this file exists to enforce:
 *
 *   1. FAIL SOFT. This sits above order history. Any failure hides the block
 *      rather than showing an error, because a waitlist outage must not look
 *      like a broken account page.
 *   2. NEVER show a pay link the server did not authorise. The server withholds
 *      the invoice URL while an edit is pending anywhere on that invoice,
 *      because paying a stale draft order charges the pre-edit amount. This file
 *      only ever renders the URL it was given; it must not reconstruct one.
 *
 * ONE INVOICE CAN COVER SEVERAL RELEASES. The store attaches a new allocation to
 * a customer's existing open draft order on purpose, so they pay once and get
 * one shipment. Money therefore belongs to the invoice, not to the queue row:
 * the server sends `invoices` separately from `lines`, and marks exactly one row
 * per invoice with `invoicePrimary`. Render the pay action there and nowhere
 * else, or the same invoice sprouts a button per release and its total is shown
 * once for each of them.
 */
class WaitlistAccount extends HTMLElement {
  connectedCallback() {
    this.titleEl = this.querySelector('.waitlist-account__title');
    this.bodyEl = this.querySelector('.waitlist-account__body');
    this.load();
  }

  t(name, fallback) {
    const v = this.dataset[name];
    return v === undefined || v === '' ? fallback || '' : v;
  }

  /**
   * A reload that fails AFTER an action succeeded must not leave the screen
   * frozen mid-action. The write already landed, so the honest thing is to say
   * it worked, give the buttons back, and let a refresh sort the rest out.
   * Returning quietly is only right on first paint, where there is nothing on
   * screen to be stuck.
   */
  bailOut() {
    if (!this.pendingNotice) return;
    this.say(this.pendingNotice.text, this.pendingNotice.kind);
    this.pendingNotice = null;
    this.querySelectorAll('.waitlist-card__btn').forEach((b) => {
      b.disabled = false;
    });
  }

  async load() {
    let data;
    try {
      const res = await fetch(this.dataset.endpoint, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return this.bailOut(); // otherwise stays hidden
      data = await res.json();
    } catch (err) {
      return this.bailOut(); // otherwise stays hidden
    }

    const lines = (data && data.lines) || [];
    if (!lines.length && this.dataset.showEmpty !== 'true') return this.bailOut();

    // Keyed by draft order id. A line only reaches for this when the server
    // flagged it as that invoice's primary row.
    this.invoices = new Map();
    ((data && data.invoices) || []).forEach((inv) => {
      this.invoices.set(inv.draftOrderId, inv);
    });

    this.titleEl.textContent = this.t('heading', 'My waitlist');
    this.bodyEl.innerHTML = '';

    // Feedback lives above the list rather than in a card, because a card is
    // rebuilt by the reload that follows every successful action.
    this.noticeEl = document.createElement('p');
    this.noticeEl.className = 'waitlist-account__notice';
    this.noticeEl.hidden = true;
    this.noticeEl.setAttribute('role', 'status');
    this.bodyEl.appendChild(this.noticeEl);
    if (this.pendingNotice) {
      this.say(this.pendingNotice.text, this.pendingNotice.kind);
      this.pendingNotice = null;
    }

    if (!lines.length) {
      this.bodyEl.appendChild(this.renderEmpty());
      this.hidden = false;
      return;
    }

    const active = lines.filter((l) => l.status === 'waiting' || l.status === 'allocated');
    const past = lines.filter((l) => l.status !== 'waiting' && l.status !== 'allocated');

    active.forEach((line) => this.bodyEl.appendChild(this.renderLine(line)));

    // Past rows are worth showing: "expired" is what explains being emailed and
    // then hearing nothing, and a row that vanishes on a lapsed claim reads as
    // the store losing their place rather than a deadline they missed.
    if (past.length) {
      const h = document.createElement('h3');
      h.className = 'waitlist-account__subtitle';
      h.textContent = this.t('pastHeading', 'Previously');
      this.bodyEl.appendChild(h);
      past.forEach((line) => this.bodyEl.appendChild(this.renderLine(line, true)));
    }

    this.hidden = false;
  }

  renderEmpty() {
    const wrap = document.createElement('div');
    wrap.className = 'waitlist-account__empty';

    const p = document.createElement('p');
    p.textContent = this.t('empty', 'You are not in line for anything right now.');
    wrap.appendChild(p);

    const url = this.dataset.browseUrl;
    if (url) {
      const a = document.createElement('a');
      a.className = 'button';
      a.href = url;
      a.textContent = this.t('browseLabel', 'Browse pre-orders');
      wrap.appendChild(a);
    }
    return wrap;
  }

  renderLine(line, isPast) {
    const card = document.createElement('div');
    card.className = 'waitlist-card' + (isPast ? ' waitlist-card--past' : '');

    const product = line.product || {};

    if (product.image) {
      const img = document.createElement('img');
      img.className = 'waitlist-card__image';
      img.src = product.image;
      img.alt = product.imageAlt || '';
      img.loading = 'lazy';
      img.width = 72;
      img.height = 72;
      card.appendChild(img);
    }

    const main = document.createElement('div');
    main.className = 'waitlist-card__main';

    // Title, linked when the product is still visible on the storefront.
    const title = document.createElement('p');
    title.className = 'waitlist-card__title';
    if (product.title && product.url) {
      const a = document.createElement('a');
      a.href = product.url;
      a.textContent = product.title;
      title.appendChild(a);
    } else {
      // The enrichment call degraded, or the product is no longer published.
      // The position is still true and still worth showing.
      title.textContent = product.title || `Product ${line.productId}`;
    }
    main.appendChild(title);

    const meta = document.createElement('p');
    meta.className = 'waitlist-card__meta';
    meta.appendChild(this.badge(line));
    meta.appendChild(document.createTextNode(' '));

    const detail = document.createElement('span');
    detail.className = 'waitlist-card__detail';
    detail.textContent = this.detailFor(line);
    meta.appendChild(detail);
    main.appendChild(meta);

    this.notesFor(line).forEach((text) => {
      const n = document.createElement('p');
      n.className = 'waitlist-card__note';
      n.textContent = text;
      main.appendChild(n);
    });

    card.appendChild(main);

    const side = this.renderActions(line, isPast);
    if (side) card.appendChild(side);

    return card;
  }

  /**
   * Pay, plus the self-service buttons when they are safe to offer.
   *
   * "Safe" is doing real work here. Every button is withheld while
   * `pendingSync` is true, which the server sets for the whole invoice, not just
   * the edited row: an edit is already on its way to Shopify and a second one
   * before the hourly job lands would race it.
   */
  renderActions(line, isPast) {
    const side = document.createElement('div');
    side.className = 'waitlist-card__action';

    const pay = this.actionFor(line);
    if (pay) side.appendChild(pay);

    const live = line.status === 'waiting' || line.status === 'allocated';
    const canAct =
      this.dataset.allowActions === 'true' && !isPast && live && !line.pendingSync && !line.paid;

    if (canAct) {
      // Lowering is always available while they hold more than one. Raising
      // never is, from either state: a position is a promise about ORDER, but an
      // allocation is spent in COPIES, so someone at #2 bumping 1 to 20 the
      // night before a drop takes the release ahead of everybody behind them
      // without ever having overtaken anybody.
      if (line.quantity > 1) {
        side.appendChild(
          this.button(this.t('labelReduce', 'Ask for fewer'), 'waitlist-card__secondary', () =>
            this.openReduce(line, side),
          ),
        );
      }

      // Allocated rows pass (release this offer, keep any part-filled
      // remainder). Waiting rows cancel outright. They are genuinely different
      // operations, so they are different buttons rather than one "remove me"
      // that would have to guess.
      if (line.status === 'allocated' && !line.passed) {
        side.appendChild(
          this.button(this.t('labelPass', 'I do not want this one'), 'waitlist-card__danger', () =>
            this.confirmPass(line),
          ),
        );
      } else if (line.status === 'waiting') {
        side.appendChild(
          this.button(this.t('labelLeave', 'Leave this waitlist'), 'waitlist-card__danger', () =>
            this.confirmLeave(line),
          ),
        );
      }
    }

    return side.childNodes.length ? side : null;
  }

  button(label, className, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `waitlist-card__btn ${className}`;
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  title(line) {
    return (line.product && line.product.title) || `#${line.position}`;
  }

  confirmLeave(line) {
    const msg = this.t('confirmLeave', 'Leave the waitlist for [TITLE]? You will lose place #[POSITION].')
      .replace('[TITLE]', this.title(line))
      .replace('[POSITION]', line.position);
    if (!window.confirm(msg)) return;
    this.send(line, 'cancel', null, this.t('labelDoneLeave', 'You have left the waitlist.').replace('[TITLE]', this.title(line)));
  }

  confirmPass(line) {
    const offered = line.allocatedQuantity === null ? line.quantity : line.allocatedQuantity;
    const msg = this.t('confirmPass', 'Release your [N] for [TITLE]?')
      .replace('[N]', `${offered} ${offered === 1 ? this.t('labelCopy', 'copy') : this.t('labelCopies', 'copies')}`)
      .replace('[TITLE]', this.title(line));
    if (!window.confirm(msg)) return;
    this.send(line, 'pass', null, this.t('labelDonePass', 'Released, thank you.'));
  }

  /**
   * A picker rather than a free number field, so an invalid quantity cannot be
   * typed at all. It only ever lists values BELOW what they hold, which is the
   * decrease-only rule expressed as a control instead of as an error message.
   */
  openReduce(line, side) {
    if (side.querySelector('.waitlist-reduce')) return;

    const wrap = document.createElement('div');
    wrap.className = 'waitlist-reduce';

    const select = document.createElement('select');
    select.className = 'waitlist-reduce__select';
    select.setAttribute('aria-label', this.t('labelReduce', 'Ask for fewer'));
    for (let n = 1; n < line.quantity; n += 1) {
      const opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = String(n);
      select.appendChild(opt);
    }
    select.value = String(line.quantity - 1);
    wrap.appendChild(select);

    wrap.appendChild(
      this.button(this.t('labelReduceApply', 'Confirm'), 'waitlist-card__secondary', () => {
        const next = Number(select.value);
        const msg = this.t('confirmReduce', 'Lower your order for [TITLE] from [FROM] to [TO]?')
          .replace('[TITLE]', this.title(line))
          .replace('[FROM]', line.quantity)
          .replace('[TO]', next);
        if (!window.confirm(msg)) return;
        this.send(line, 'reduce', next, this.t('labelDoneReduce', 'Updated.'));
      }),
    );
    wrap.appendChild(
      this.button(this.t('labelReduceCancel', 'Cancel'), 'waitlist-card__secondary', () => wrap.remove()),
    );

    side.appendChild(wrap);
  }

  say(text, kind) {
    if (!this.noticeEl) return;
    this.noticeEl.textContent = text;
    this.noticeEl.hidden = !text;
    this.noticeEl.classList.toggle('waitlist-account__notice--error', kind === 'error');
  }

  /**
   * Post an intent and re-read the list.
   *
   * Reloading rather than patching the card locally is deliberate: the server
   * decides what is now payable, what a part-fill left owed, and which invoice a
   * pending edit has frozen. Guessing any of that here is how the screen ends up
   * disagreeing with the invoice.
   */
  async send(line, action, quantity, doneText) {
    this.querySelectorAll('.waitlist-card__btn').forEach((b) => {
      b.disabled = true;
    });
    this.say(this.t('labelWorking', 'Working...'), '');

    let data;
    try {
      const res = await fetch(this.dataset.actionEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          product_id: line.productId,
          action,
          quantity: quantity === null ? undefined : quantity,
        }),
      });
      data = await res.json();
      if (!res.ok || data.ok !== true) throw new Error(data && data.error);
    } catch (err) {
      this.say(this.t('labelActionError', 'We could not do that just now. Nothing has been changed.'), 'error');
      this.querySelectorAll('.waitlist-card__btn').forEach((b) => {
        b.disabled = false;
      });
      return;
    }

    // Survives the re-render that load() performs.
    this.pendingNotice = { text: doneText, kind: '' };
    this.load();
  }

  badge(line) {
    const span = document.createElement('span');
    const map = {
      waiting: ['statusWaiting', 'Waiting'],
      allocated: ['statusAllocated', 'Ready to claim'],
      purchased: ['statusPurchased', 'Bought'],
      expired: ['statusExpired', 'Window closed'],
      cancelled: ['statusCancelled', 'Removed'],
    };
    const entry = map[line.status] || ['statusWaiting', line.status];
    span.className = `waitlist-badge waitlist-badge--${line.status}`;
    span.textContent = this.t(entry[0], entry[1]);
    return span;
  }

  /** "#7 in line, 2 copies" */
  detailFor(line) {
    const copies = line.quantity === 1 ? this.t('labelCopy', 'copy') : this.t('labelCopies', 'copies');
    const position = this.t('labelPosition', 'in line').replace('[POSITION]', line.position);
    return `#${line.position} ${position}, ${line.quantity} ${copies}`;
  }

  notesFor(line) {
    const notes = [];

    // A pending edit is the reason the pay button is missing. Say so, or the
    // page looks broken to the one person who just used a self-service button.
    // Note this is invoice-wide: a sibling release's pending edit reaches here
    // too, which is correct, because paying settles every line at once.
    if (line.pendingSync) {
      notes.push(this.t('labelUpdating', 'We are updating your invoice. Please wait for our email before paying it.'));
    } else if (line.paid) {
      notes.push(this.t('labelPaid', 'Paid. Thank you.'));
    } else if (line.status === 'allocated') {
      // A part-fill: invoiced for what arrived, keeps the position for the rest.
      if (line.allocatedQuantity !== null && line.allocatedQuantity < line.quantity) {
        notes.push(
          this.t('labelPartFill', 'We could only offer [N] of these. You keep your place for the rest.')
            .replace('[N]', line.allocatedQuantity),
        );
      } else if (line.claimLockExempt || !line.claimExpiresAt) {
        // The affiliate band claims with no deadline; a blank countdown there is
        // correct rather than missing data.
        notes.push(this.t('labelNoDeadline', 'No deadline on this one.'));
      } else {
        notes.push(this.countdown(line.claimExpiresAt));
      }
    }

    // The pay button and the total belong to an invoice that may cover more than
    // this one release. Unlabelled, the amount reads as the price of the item
    // they are looking at, which is wrong and wrong in the alarming direction.
    // Each release keeps its OWN deadline, so this is not merely cosmetic: the
    // soonest of them is what the invoice has to be paid by.
    const invoice = this.invoiceFor(line);
    if (invoice && invoice.releases > 1) {
      notes.push(
        this.t('labelSharedInvoice', 'One invoice, covering [N] of your releases.')
          .replace('[N]', invoice.releases),
      );
    }

    return notes.filter(Boolean);
  }

  countdown(iso) {
    const ms = new Date(iso).getTime() - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) {
      return this.t('labelExpiresSoon', 'Your claim window has closed.');
    }
    const hours = Math.floor(ms / 3600000);
    const value = hours >= 1 ? `${hours}h` : `${Math.max(1, Math.floor(ms / 60000))}m`;
    return this.t('labelExpires', 'Claim within [TIME].').replace('[TIME]', value);
  }

  /** The invoice this row is responsible for rendering, if any. */
  invoiceFor(line) {
    if (!line.invoicePrimary || line.draftOrderId === null) return null;
    return this.invoices.get(line.draftOrderId) || null;
  }

  actionFor(line) {
    const invoice = this.invoiceFor(line);
    // Only ever the URL the server handed over. It withholds this whenever an
    // edit is pending on ANY line of the invoice, precisely so a stale amount
    // cannot be paid.
    if (!invoice || !invoice.url) return null;

    const a = document.createElement('a');
    a.className = 'button waitlist-card__pay';
    a.href = invoice.url;
    a.textContent = this.t('labelPay', 'Pay now');
    return a;
  }
}

customElements.define('waitlist-account', WaitlistAccount);
